import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import SectionHeader from '@/components/feature/SectionHeader';
import { getHotMovies, searchMovies } from '@/utils/movie';
import { usePageMeta } from '@/hooks/usePageMeta';

const recommend = getHotMovies(6);

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('keyword') ?? '';
  const [input, setInput] = useState(keyword);

  usePageMeta(
    keyword ? `搜索“${keyword}” - 亚洲中文字幕在线观看` : '搜索影片 - 亚洲中文字幕在线观看',
    keyword
      ? `在亚洲中文字幕在线观看搜索“${keyword}”的相关影视作品。`
      : '搜索你想看的亚洲影视作品，支持标题、简介、演员等关键词匹配。',
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = keyword ? searchMovies(keyword) : [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { keyword: value } : {});
  };

  return (
    <Layout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
            搜索影视作品
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入影片名称、演员或关键词，找到你想看的亚洲影视内容
          </p>
          <form onSubmit={handleSubmit} className="mt-5">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-lg p-1.5 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500 text-lg ml-2"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：悬疑、韩国、长夜将明…"
                className="flex-1 bg-transparent outline-none text-sm md:text-base text-foreground-900 placeholder:text-foreground-500 px-2 h-10"
              />
              <button
                type="submit"
                className="px-5 h-10 rounded-md bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors"
              >
                搜索
              </button>
            </div>
          </form>
        </div>

        {keyword && (
          <div className="mt-8">
            <p className="text-sm text-foreground-500 mb-5">
              关键词 <strong className="text-primary-500">“{keyword}”</strong> 共找到{' '}
              <strong className="text-foreground-900">{results.length}</strong> 部相关作品
            </p>
            {results.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <>
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到匹配的作品"
                  description="换个关键词试试，或者看看下面这些热门推荐。"
                />
                <div className="mt-6">
                  <SectionHeader title="热门推荐" subtitle="大家都在看的高分作品" icon="ri-fire-line" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                    {recommend.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!keyword && (
          <div className="mt-10">
            <SectionHeader title="大家都在搜" subtitle="热门关键词，一键直达" icon="ri-search-line" />
            <div className="flex flex-wrap gap-2">
              {['悬疑', '韩国', '爱情', '古装', '日本', '科幻', '犯罪', '青春'].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?keyword=${encodeURIComponent(tag)}`}
                  className="px-4 h-9 flex items-center rounded-full bg-background-100 border border-background-200 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-500 cursor-pointer whitespace-nowrap transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <SectionHeader title="热门影视推荐" subtitle="不知道看什么？从这里开始" icon="ri-fire-line" moreTo="/category/hot" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                {recommend.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}